import {
  uploadInvoices,
  getAllPurchases,
  getPurchaseDetail,
  deletePurchase,
  movePurchase,
  previewMovePurchase,
  updateInvoice,
  setDeductibleProduct,
} from '../../services/purchase'
import { getPaymentMethods, getCategories } from '../../services/invoice'
import { ref } from 'vue'

export type Pagination = {
  page: number
  perPage: number
  total: number
}

export type Sorting = {
  sortBy: keyof any | undefined
  sortingOrder: 'asc' | 'desc' | null
}

export type Filters = {
  isActive: boolean
  search: string
  category: number | null
  month: string
  documentType: string
  year: number
  assignment?: string | null
}
export const globalData = ref(null)
export const years = ref<any[]>([])
const currentLoadedData = ref(null)
const getSortItem = (obj: any, sortBy: string) => {
  return obj[sortBy]
}

function lineMatchesAssignment(lines: any[] | undefined, assignment: string) {
  const rows = lines || []
  if (assignment === 'none') {
    return rows.some((line) => !line.budgetItemId && !line.fundId)
  }
  if (assignment.startsWith('b-')) {
    return rows.some((line) => `b-${line.budgetItemId}` === assignment)
  }
  if (assignment.startsWith('f-')) {
    return rows.some((line) => `f-${line.fundId}` === assignment)
  }
  if (assignment.startsWith('c-')) {
    return rows.some((line) => `c-${line.category ?? line.purchaseCategory?.id}` === assignment)
  }
  return true
}
export const getPurchases = async (
  filters: Partial<Filters & Pagination & Sorting>,
  loadOther = false,
  categoryMap?: Map<number, string>,
) => {
  let purchaseData
  //it will load invoices or other incomings documents
  if (currentLoadedData.value !== loadOther) globalData.value = null
  currentLoadedData.value = loadOther as any
  if (!globalData.value) {
    const { data } = await getAllPurchases(loadOther)
    globalData.value = data
    purchaseData = data
    years.value = [
      ...new Set(
        purchaseData.map((p: any) => {
          return parseInt(p.date.split('-')[0])
        }),
      ),
    ]
  } else {
    purchaseData = globalData.value
  }
  const { search, sortBy, sortingOrder, month, documentType, year, assignment } = filters
  let filteredInvoices = purchaseData
  if (year) {
    filteredInvoices = filteredInvoices.filter((invoice: any) => {
      const splitDate = invoice.date.split('-')
      return splitDate[0] === `${year}`
    })
  }
  if (month && month !== '-1') {
    filteredInvoices = filteredInvoices.filter((invoice: any) => {
      const splitDate = invoice.date.split('-')
      return splitDate[1] === month
    })
  }

  if (documentType && documentType !== 'TODOS') {
    filteredInvoices = filteredInvoices.filter(
      (invoice: any) => invoice.purchaseDocumentType.name.toLowerCase() === documentType.toLowerCase(),
    )
  }

  if (search) {
    const searchLower = search.toLowerCase()

    filteredInvoices = filteredInvoices.filter((invoice: any) => {
      const matchEmisor = invoice.emisor?.toLowerCase().includes(searchLower)

      const matchAdditional = JSON.stringify(invoice.additionalInfo || {})
        .toLowerCase()
        .includes(searchLower)

      const matchTotal = invoice.total?.toString().includes(searchLower)

      const matchDate = invoice.date?.toString().toLowerCase().includes(searchLower)

      const matchProducts = invoice.purchaseList?.some((product: any) => {
        const detailMatch = product.detail?.toLowerCase().includes(searchLower)

        const categoryLabel = categoryMap?.get(product.category)?.includes(searchLower)

        return detailMatch || categoryLabel
      })

      return matchEmisor || matchAdditional || matchProducts || matchTotal || matchDate
    })
  }

  if (loadOther && assignment) {
    filteredInvoices = filteredInvoices.filter((invoice: any) =>
      lineMatchesAssignment(invoice.purchaseList, assignment),
    )
  }

  filteredInvoices = filteredInvoices.map((invoice: any) => ({ ...invoice }))

  if (sortBy && sortingOrder) {
    filteredInvoices = filteredInvoices.sort((a: any, b: any) => {
      const first = getSortItem(a, sortBy)
      const second = getSortItem(b, sortBy)
      if (first > second) {
        return sortingOrder === 'asc' ? 1 : -1
      }
      if (first < second) {
        return sortingOrder === 'asc' ? -1 : 1
      }
      return 0
    })
  }

  const { page = 1, perPage = 10 } = filters || {}
  return {
    data: filteredInvoices.slice((page - 1) * perPage, page * perPage),
    pagination: {
      page,
      perPage,
      total: filteredInvoices.length,
    },
  }
}

export const uploadInvoicesAPI = async (data: any) => {
  try {
    const resp = await uploadInvoices(data)
    if (resp.status === 200 && resp.data) {
      return resp.data
    }
  } catch (e) {
    console.error(e)
    throw e
  }
}

export const getDetail = async (id: string) => {
  try {
    const { data } = await getPurchaseDetail(id)
    return data
  } catch (e) {
    throw e
  }
}

export const previewPurchaseMove = async (id: string, workspaceId: number) => {
  return previewMovePurchase(id, workspaceId)
}

export const movePurchaseWorkspace = async (
  id: string,
  workspaceId: number,
  choices: {
    accountId?: number | null
    categoryId?: number | null
    budgetItemId?: number | null
    fundId?: number | null
  },
) => {
  await movePurchase(id, workspaceId, choices)
  globalData.value = null
}

export const removeInvoice = async (id: string) => {
  try {
    await deletePurchase(id)
    globalData.value = null
    return true
  } catch (e) {
    throw e
  }
}
export const getPaymentMethodsAPI = async () => {
  try {
    const { data } = await getPaymentMethods()
    return data
  } catch (e) {
    throw e
  }
}

export const setDeductibleProductAPI = async (data: { detail?: string; cleanDetail?: string; deductible: boolean }) => {
  const resp = await setDeductibleProduct(data)
  if (resp.status === 200 && resp.data) {
    return resp.data
  }
  throw new Error('No se pudo actualizar el producto deducible.')
}

export const updateInvoiceAPI = async (data: any) => {
  try {
    const resp = await updateInvoice(data)
    if (resp.status === 200 && resp.data) {
      return resp.data
    }
  } catch (e) {
    console.error(e)
    throw e
  }
}
export const getAllCategories = async () => {
  try {
    const { data } = await getCategories('E')
    return data
  } catch (e) {
    console.error(e)
    throw e
  }
}
export const cleanPurchases = async () => {
  try {
    globalData.value = null
    return true
  } catch (e) {
    console.error(e)
    throw e
  }
}
